import { useEffect, useRef, useState } from 'react'
import { Filter } from 'lucide-react'
import type { Requirement, RequirementTheme } from '@ispot/shared'
import { useRequirementsViewStore } from '@/stores/requirementsViewStore'
import { FilterPopover } from './FilterPopover'

interface Props {
  requirements: Requirement[]
  themes: RequirementTheme[]
}

/**
 * Pill button with active filter count + anchored popover. Drop in any toolbar.
 *
 * Owns open/close state AND outside-click detection. The outside-click ignores
 * the entire wrapper (button + popover), so re-clicking the button to close
 * doesn't race with FilterPopover's own close handler.
 */
export function FilterButton({ requirements, themes }: Props) {
  const [open, setOpen] = useState(false)
  const wrapRef = useRef<HTMLDivElement | null>(null)
  const facetTypes = useRequirementsViewStore(s => s.facetTypes)
  const facetPriorities = useRequirementsViewStore(s => s.facetPriorities)
  const facetStatuses = useRequirementsViewStore(s => s.facetStatuses)
  const facetThemeIds = useRequirementsViewStore(s => s.facetThemeIds)
  const total = facetTypes.size + facetPriorities.size + facetStatuses.size + facetThemeIds.size

  // Outside-click + ESC. Single source of truth for closing.
  useEffect(() => {
    if (!open) return
    function onPointer(e: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false)
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onPointer)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onPointer)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  return (
    <div ref={wrapRef} className="relative inline-block">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        aria-haspopup="dialog"
        aria-expanded={open}
        className="inline-flex items-center gap-1.5 rounded-lg cursor-pointer font-medium px-3 py-1.5 text-sm transition-colors"
        style={{
          background: total > 0 ? 'var(--color-warm-100)' : 'var(--color-bg-card)',
          border: `1px solid var(--color-warm-200)`,
          color: 'var(--color-fg)',
        }}
        onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--color-warm-100)')}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = total > 0 ? 'var(--color-warm-100)' : 'var(--color-bg-card)'
        }}
      >
        <Filter className="h-4 w-4" style={{ color: 'var(--color-fg-muted)' }} />
        Filter
        {total > 0 && (
          <span
            className="font-mono tabular-nums inline-flex items-center justify-center"
            style={{
              fontSize: 11,
              fontWeight: 600,
              padding: '0 6px',
              minWidth: 18,
              height: 18,
              borderRadius: 999,
              background: 'var(--color-solution)',
              color: 'var(--color-fg)',
            }}
          >
            {total}
          </span>
        )}
      </button>
      {open && (
        <div
          className="absolute z-30"
          style={{ top: 'calc(100% + 6px)', left: 0 }}
        >
          <FilterPopover requirements={requirements} themes={themes} />
        </div>
      )}
    </div>
  )
}
